import React, { useEffect, useState, useCallback } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import Pagination from '../../../components/Pagination';
import ListingFieldChanges from '../../../components/listingDetail/Overview/ListingFieldChanges';
import PhaseASectionReview from '../../../components/listingDetail/Overview/PhaseASectionReview';
import client from '../../../lib/feathers';

const LIMIT = 25;

// Pull the few human-relevant signals out of the verbose stage-03 import
// proposal text (counts, dedup-care, contamination) so the card can lead with
// a summary instead of a wall of text. (Stage 03 is text today; when it emits a
// structured summary this can read that instead.)
function parse03(text) {
  const out = { contamination: [] };
  if (!text) return out;
  const mw = text.match(/\((\d+) libations \/ (\d+) vintages\)/);
  if (mw) { out.libations = +mw[1]; out.vintages = +mw[2]; }
  const mu = text.match(/(\d+) libation\(s\) on this listing have user data/);
  if (mu) out.userData = +mu[1];
  out.contamination = text.split('\n')
    .filter((l) => l.includes('leads with another producer'))
    .map((l) => {
      const m = l.match(/\s(.+?)\s+(?:—|-)\s+leads with another producer/);
      return m ? m[1].trim() : null;
    })
    .filter(Boolean);
  return out;
}

// Fallback only. The server computes this now and stores it on the row as
// `reviewFlags` (api/src/lib/enrichment-run-review.js), which is what lets the
// queue sort, filter and count without loading every row. This local copy
// covers rows written before the backfill ran, so the page stays correct in
// the window between deploying and running it.
function runBlockers(stages) {
  const blockers = [];
  const pd = stages['05_listing_fields'] && stages['05_listing_fields'].proposalData;
  for (const f of (pd && pd.flags) || []) {
    if (f.type === 'name_mismatch') blockers.push(`Name mismatch: "${f.old}" -> "${f.new}"`);
  }
  const vid = stages.verify_identity;
  if (vid && vid.status === 'misbonded') blockers.push(`Identity misbonded (${vid.confidence || '?'})`);
  const s3 = parse03(stages['03_import'] && stages['03_import'].proposal);
  if (s3.contamination.length) {
    blockers.push(`${s3.contamination.length} possible contamination flag(s): ${s3.contamination.slice(0, 3).join(', ')}${s3.contamination.length > 3 ? '...' : ''}`);
  }
  return blockers;
}

function blockersFor(run, stages) {
  return Array.isArray(run.reviewFlags) ? run.reviewFlags : runBlockers(stages);
}

/**
 * Pending Approval view: full-chain runs that finished their read-only stages
 * and produced a proposed plan, but have not been approved for the
 * prod-writing stages yet.
 *
 * Paginated server-side. This page used to walk every awaiting_review row on
 * mount (about 25 sequential requests and 20MB once the queue passed a
 * thousand rows) purely so it could sort blockers first and count them in the
 * browser. The server stores that verdict on the row now, so one request per
 * screen does the job.
 *
 * The URL drives the view, so a filtered queue is linkable and survives a
 * refresh:
 *   ?page=      zero-indexed page
 *   ?q=         winery name search
 *   ?show=needs only rows with blockers
 *   ?show=clean only rows without them
 *   ?runId=     pin a single run (how the listing page deep-links in)
 */
export default function ListingDataHealthPendingApproval() {
  const navigate = useNavigate();
  const [query] = useSearchParams();

  const page = Number(query.get('page') || 0);
  const runId = query.get('runId') || '';
  const show = query.get('show') || '';  // '' | 'needs' | 'clean'
  const needsOnly = show === 'needs';
  const cleanOnly = show === 'clean';
  const q = query.get('q') || '';

  const [runs, setRuns] = useState(null);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ need: 0, clean: 0 });
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => { setSearchInput(q); }, [q]);

  const load = useCallback(async () => {
    setRuns(null);
    try {
      const service = client.service('admin/listing-enrichment-run');

      // Deep link from a listing: show just that run, so the operator lands on
      // the thing they clicked rather than the top of a thousand-row queue.
      if (runId) {
        const run = await service.get(runId);
        const blockers = blockersFor(run, run.stages || {});
        setRuns([run]);
        setTotal(1);
        setCounts({ need: blockers.length ? 1 : 0, clean: blockers.length ? 0 : 1 });
        setError(null);
        return;
      }

      // `unapproved` is translated to `approvedAt IS NULL` server-side.
      // Sending a literal null here does not work: feathers-rest serializes it
      // as the string "null" and Postgres 500s coercing that to a timestamp.
      const base = { outcome: 'awaiting_review', unapproved: true };
      if (q) base.listingName = q;

      const [result, needRes, cleanRes] = await Promise.all([
        service.find({
          query: {
            ...base,
            ...(needsOnly ? { needsReview: true } : {}),
            ...(cleanOnly ? { needsReview: false } : {}),
            // Blockers first, then newest finished. A re-run (say after
            // fixing a website) should surface above older rows.
            $sort: { needsReview: -1, finishedAt: -1 },
            $limit: LIMIT,
            $skip: page * LIMIT,
          },
        }),
        service.find({ query: { ...base, needsReview: true, $limit: 0 } }),
        service.find({ query: { ...base, needsReview: false, $limit: 0 } }),
      ]);

      setRuns(result.data || []);
      setTotal(result.total || 0);
      setCounts({ need: needRes.total || 0, clean: cleanRes.total || 0 });
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load pending-approval queue');
    }
  }, [page, q, show, runId]);

  useEffect(() => {
    load();
  }, [load]);

  // One place builds the URL, so page, search and filter never disagree.
  const go = (next = {}) => {
    const params = new URLSearchParams();
    const merged = {
      page: next.page !== undefined ? next.page : page,
      q: next.q !== undefined ? next.q : q,
      show: next.show !== undefined ? next.show : show,
      runId: next.runId !== undefined ? next.runId : runId,
    };
    if (merged.runId) params.set('runId', merged.runId);
    if (merged.q) params.set('q', merged.q);
    if (merged.show) params.set('show', merged.show);
    if (merged.page) params.set('page', String(merged.page));
    const qs = params.toString();
    navigate(`/admin/reports/listing-data-health/pending-approval${qs ? `?${qs}` : ''}`);
  };

  const navigateToPage = (p = 0) => {
    const n = Number(p);
    if (n < 0 || n * LIMIT >= total) return;
    go({ page: n });
  };

  const submitSearch = (e) => {
    e.preventDefault();
    // A new search invalidates the current page and any pinned run.
    go({ q: searchInput.trim(), page: 0, runId: '' });
  };

  // Drop a row from view once actioned. Cheaper than a refetch, and it keeps
  // the operator's scroll position while working down a page.
  const removeRow = (id) => {
    setRuns((cur) => (cur || []).filter((r) => r.id !== id));
    setTotal((t) => Math.max(0, t - 1));
  };

  const approve = async (id) => {
    if (!window.confirm('Approve this run? The supervisor will write the proposed changes to prod on its next tick.')) return;
    setBusyId(id);
    try {
      // Server stamps approvedByUserId from auth context: we just send the
      // timestamp. Avoids the prod bug where me.id from Redux was sometimes
      // undefined and the patch silently no-op'd.
      const patched = await client.service('admin/listing-enrichment-run').patch(id, {
        approvedAt: new Date().toISOString(),
      });
      if (!patched || !patched.approvedAt) {
        throw new Error('approvedAt did not persist on the server, patch was a no-op');
      }
      removeRow(id);
    } catch (e) {
      alert(`Approve failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  // Per-phase approve: apply just listing fields (A) or just wines (B). The
  // supervisor runs only the approved phase. A row may have one phase
  // auto-approved already; this lets the operator action the other.
  const approvePhase = async (id, phase) => {
    const label = phase === 'A' ? 'listing fields' : 'wines';
    if (!window.confirm(`Apply ${label} for this run? The supervisor will write them to prod on its next tick.`)) return;
    setBusyId(id);
    try {
      const field = phase === 'A' ? 'phaseAApprovedAt' : 'phaseBApprovedAt';
      const patched = await client.service('admin/listing-enrichment-run').patch(id, {
        [field]: new Date().toISOString(),
      });
      if (!patched || !patched[field]) {
        throw new Error(`${field} did not persist, patch was a no-op`);
      }
      removeRow(id);
    } catch (e) {
      alert(`Apply ${label} failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  // Reject keeps the proposal text on the row for audit but moves the row out
  // of awaiting_review so it stops cluttering the queue. The outcome flips to
  // 'skipped' with an explanatory error. (Not adding a new 'rejected' enum
  // value: overloaded 'skipped' works fine and avoids a migration.)
  const reject = async (id) => {
    const reason = window.prompt('Reject this proposal? Add a short reason (will be saved on the row):', 'admin rejected');
    if (reason === null) return;  // operator cancelled the prompt
    setBusyId(id);
    try {
      await client.service('admin/listing-enrichment-run').patch(id, {
        outcome: 'skipped',
        error: `admin rejected: ${reason || 'no reason given'}`,
        finishedAt: new Date().toISOString(),
      });
      removeRow(id);
    } catch (e) {
      alert(`Reject failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = !!(q || show || runId);

  // Clicking a count filters to it; clicking it again clears. Cheaper than a
  // separate control, and it puts the filter on the number the operator is
  // already reading.
  const countButton = (key, label, hint, tone) => {
    const active = show === key;
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => go({ show: active ? '' : key, page: 0, runId: '' })}
        title={active ? 'Showing only these. Click again to clear.' : hint}
        className={`rounded px-2 py-0.5 ${tone.text} hover:underline ${active ? `font-semibold ${tone.activeBg} ring-1 ${tone.activeRing}` : ''}`}
      >
        {label}
      </button>
    );
  };

  // Shown inside the pagination bar's left slot when the queue spans more than
  // one page, and on its own line when it does not.
  const countsLine = (
    <div className="text-sm text-gray-600 -ml-2">
      {countButton('needs', `${counts.need} need review`, 'Show only runs with blockers that need a human decision.', {
        text: 'text-amber-700', activeBg: 'bg-amber-50', activeRing: 'ring-amber-300',
      })}
      <span className="mx-1 text-gray-300">&middot;</span>
      {countButton('clean', `${counts.clean} look clean`, 'Show only runs with nothing flagged, ready to approve.', {
        text: 'text-green-700', activeBg: 'bg-green-50', activeRing: 'ring-green-300',
      })}
      {q && <span className="ml-2 text-gray-500">matching &ldquo;{q}&rdquo;</span>}
    </div>
  );
  const paginated = !runId && total > LIMIT;

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Reports', href: '/admin/reports' },
            { name: 'Listing Data Health', href: '/admin/reports/listing-data-health' },
            { name: 'Pending Approval' },
          ]} />
        </nav>
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-3">Pending Approval</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          Full-chain runs that finished their read-only stages (crawl / extract / normalize) and produced a proposed plan for stages 03 + 04 (the prod-writing ones). Review the proposal and click <strong>Approve</strong> to let the supervisor run the writes on its next tick. The row stays here indefinitely until approved.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form onSubmit={submitSearch} className="flex items-center gap-2">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by winery name"
            className="w-64 border-gray-300 rounded-md shadow-sm text-sm focus:ring-primary focus:border-primary"
          />
          <button
            type="submit"
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Search
          </button>
        </form>

        {filtered && (
          <button
            type="button"
            onClick={() => navigate('/admin/reports/listing-data-health/pending-approval')}
            className="text-sm text-indigo-600 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {runId && (
        <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-md px-4 py-2 text-sm text-indigo-900">
          Showing one run, linked from a listing.{' '}
          <button
            type="button"
            onClick={() => navigate('/admin/reports/listing-data-health/pending-approval')}
            className="underline font-medium"
          >
            See the whole queue
          </button>
        </div>
      )}

      {error && <div className="text-sm text-red-700 mb-4">{error}</div>}
      {!error && runs === null && <div className="text-sm text-gray-500">Loading...</div>}

      {!error && runs && !runId && !paginated && (
        <div className="mb-3">{countsLine}</div>
      )}

      {!error && runs && runs.length === 0 && (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500 italic">
          {filtered
            ? 'No runs match this search. Try a different name, or clear the filters.'
            : 'Nothing waiting for approval. New full-chain runs land here after their read-only stages complete.'}
        </div>
      )}

      {!error && runs && runs.length > 0 && (
        <>
          {paginated && (
            <div className="bg-white shadow rounded-lg mb-4">
              <Pagination total={total} limit={LIMIT} page={page} navigateToPage={navigateToPage} leading={countsLine} />
            </div>
          )}

          <div className="space-y-4">
            {runs.map((r) => {
              const stages = (r.stages && typeof r.stages === 'object') ? r.stages : {};
              const blockers = blockersFor(r, stages);
              const proposal03 = stages['03_import']?.proposal;
              const proposal04 = stages['04_upload_assets']?.proposal;
              const listingChanges = stages['05_listing_fields']?.proposalData;
              const s3 = parse03(proposal03);
              const phaseACount = listingChanges
                ? (listingChanges.fills?.length || 0) + (listingChanges.overwrites?.length || 0)
                : 0;
              const clean = blockers.length === 0;
              return (
                <div key={r.id} className={`bg-white shadow rounded-lg overflow-hidden border-l-4 ${clean ? 'border-green-400' : 'border-amber-400'}`}>
                  <div className="px-4 py-3 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start sm:gap-4">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 break-words">
                        {r.listing ? (
                          <NavLink to={`/admin/listings/${r.listing.id}`} className="text-indigo-700 hover:underline">
                            {r.listing.name}
                          </NavLink>
                        ) : (
                          <span className="text-gray-400 italic">listing unknown</span>
                        )}
                      </h3>
                      <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-gray-500">
                        <span>Ran {r.finishedAt ? formatDistanceToNow(new Date(r.finishedAt), { addSuffix: true }) : 'at an unknown time'}</span>
                        <span>&middot; trigger {r.triggeredBy}</span>
                        <NavLink to={`/admin/reports/listing-data-health/run/${r.id}`} className="text-indigo-600 hover:underline">&middot; steps &rarr;</NavLink>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                      <button type="button" onClick={() => reject(r.id)} disabled={busyId === r.id}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        title="Drop this proposed run without writing.">
                        Reject
                      </button>
                      {/* Legacy all-or-nothing Phase-A apply: only for old runs
                          without category sections; new runs use the per-category
                          panel below. */}
                      {!listingChanges?.sections?.length && (
                        <button type="button" onClick={() => approvePhase(r.id, 'A')} disabled={busyId === r.id}
                          className="inline-flex items-center px-3 py-2 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white disabled:opacity-50"
                          title="Apply only the listing fields (address / hours / website / socials).">
                          Apply listing fields
                        </button>
                      )}
                      <button type="button" onClick={() => approvePhase(r.id, 'B')} disabled={busyId === r.id}
                        className="inline-flex items-center px-3 py-2 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white disabled:opacity-50"
                        title="Apply only the wines (libations + vintages + assets).">
                        Apply wines
                      </button>
                      <button type="button" onClick={() => approve(r.id)} disabled={busyId === r.id}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50">
                        {busyId === r.id ? 'Approving...' : 'Approve both'}
                      </button>
                    </div>
                  </div>

                  {/* Verdict + at-a-glance summary: the part you actually scan */}
                  <div className="px-4 py-3">
                    {clean ? (
                      <div className="text-sm font-medium text-green-700">✓ Looks clean, safe to approve</div>
                    ) : (
                      <div>
                        <div className="text-sm font-semibold text-amber-800 mb-1">⚠ Needs review</div>
                        <ul className="list-disc list-inside text-sm text-amber-900 space-y-0.5">
                          {blockers.map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                      {s3.libations != null && <span>📦 {s3.libations} wines · {s3.vintages} vintages</span>}
                      {s3.userData ? <span className="text-gray-500">👥 {s3.userData} with user data (dedup care)</span> : null}
                      {phaseACount ? <span>📝 Phase A: {phaseACount} field change(s)</span> : null}
                      {!listingChanges && <span className="text-gray-400 italic">no Phase-A data (pre-fix run)</span>}
                    </div>
                  </div>

                  {/* Phase-A listing fields. New runs carry category `sections`
                      so they get an interactive per-category accept; older runs
                      fall back to the read-only view plus the all-or-nothing
                      button above. */}
                  {listingChanges && (
                    <div className="px-4 pb-3">
                      {listingChanges.sections?.length ? (
                        <PhaseASectionReview
                          run={r}
                          sections={listingChanges.sections}
                          hasWines={(s3.libations || 0) > 0}
                          onDone={(id, cleared) => { if (cleared) removeRow(id); }}
                        />
                      ) : (
                        <ListingFieldChanges proposalData={listingChanges} />
                      )}
                    </div>
                  )}

                  {/* Verbose stage 03/04 proposals, collapsed (open by default only when review needed) */}
                  <details open={!clean} className="px-4 py-2 border-t border-gray-100">
                    <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">Full proposal (stage 03 import / 04 assets)</summary>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Stage 03: Import wines</h4>
                        {proposal03 ? (
                          <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-64">{proposal03}</pre>
                        ) : <div className="text-xs text-gray-400 italic">no proposal captured</div>}
                      </div>
                      <div>
                        <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Stage 04: Upload assets</h4>
                        {proposal04 ? (
                          <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-64">{proposal04}</pre>
                        ) : <div className="text-xs text-gray-400 italic">no proposal captured</div>}
                      </div>
                    </div>
                  </details>

                  {/* Stage breakdown chips */}
                  <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs flex flex-wrap gap-2">
                    {Object.entries(stages).map(([k, v]) => (
                      <span key={k} className={`px-2 py-0.5 rounded font-mono ${v?.ok === true ? 'bg-green-50 text-green-700' : v?.ok === false ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {k} {v?.ok === true ? '✓' : v?.ok === false ? '✗' : '?'}{v?.durationSec != null ? ` ${v.durationSec}s` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {paginated && (
            <div className="bg-white shadow rounded-lg mt-4">
              <Pagination total={total} limit={LIMIT} page={page} navigateToPage={navigateToPage} />
            </div>
          )}
        </>
      )}
    </Page>
  );
}
